// src/pages/ia-paca/quiz-ai-readiness.tsx
//
// Quiz AI Readiness Score — lead magnet interactif.
// 15 questions sur la maturité IA de l'entreprise, scoring automatique
// (5 dimensions : stratégie, data, équipe, infra, gouvernance), rapport
// personnalisé envoyé par email + persistance lead Supabase.

import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import { supabase } from '../../lib/supabase';
import {
  ArrowLeft,
  ArrowRight,
  ArrowLeftCircle,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Target,
  Database,
  Users,
  Cpu,
  ShieldCheck,
  Mail,
  Download,
  TrendingUp,
} from 'lucide-react';

// =========================================================================
// Modèle de quiz
// =========================================================================

type Dimension = 'strategy' | 'data' | 'team' | 'infra' | 'governance';

interface Choice {
  label: string;
  score: number; // 0 à 4
}

interface Question {
  dimension: Dimension;
  question: string;
  hint?: string;
  choices: Choice[];
}

const DIMENSIONS: { key: Dimension; label: string; Icon: typeof Target; color: string }[] = [
  { key: 'strategy', label: 'Stratégie & cas d\'usage', Icon: Target, color: 'pink' },
  { key: 'data', label: 'Données', Icon: Database, color: 'violet' },
  { key: 'team', label: 'Équipe & compétences', Icon: Users, color: 'orange' },
  { key: 'infra', label: 'Infra & outillage', Icon: Cpu, color: 'emerald' },
  { key: 'governance', label: 'Gouvernance & risques', Icon: ShieldCheck, color: 'blue' },
];

const QUESTIONS: Question[] = [
  // STRATÉGIE
  {
    dimension: 'strategy',
    question: "Votre entreprise a-t-elle identifié des cas d'usage IA prioritaires liés à votre cœur de métier ?",
    choices: [
      { label: 'Aucun, on n\'a pas encore réfléchi', score: 0 },
      { label: 'On a une intuition mais rien de formalisé', score: 1 },
      { label: 'On a une liste de cas, sans prioritisation claire', score: 2 },
      { label: 'On a 2-3 cas prioritaires, validés par la direction', score: 3 },
      { label: 'Roadmap IA structurée, budgétée, suivie par un sponsor', score: 4 },
    ],
  },
  {
    dimension: 'strategy',
    question: "Avez-vous chiffré l'impact business attendu d'au moins un projet IA ?",
    choices: [
      { label: 'Non, ce n\'est pas mesuré', score: 0 },
      { label: 'On a une idée vague (gain de temps, productivité)', score: 1 },
      { label: 'On a une fourchette estimée (gain € ou % conversion)', score: 2 },
      { label: 'On a un business case complet sur 1 projet', score: 3 },
      { label: 'Business cases multiples, suivis vs réel post-déploiement', score: 4 },
    ],
  },
  {
    dimension: 'strategy',
    question: "Avez-vous déjà déployé un produit ou une fonctionnalité IA en production (au-delà du POC) ?",
    choices: [
      { label: 'Non, jamais', score: 0 },
      { label: 'POC en cours, pas encore en prod', score: 1 },
      { label: '1 fonctionnalité en prod (interne)', score: 2 },
      { label: '1+ produit en prod côté client', score: 3 },
      { label: 'Plusieurs produits en prod, IA dans le cœur de l\'offre', score: 4 },
    ],
  },

  // DATA
  {
    dimension: 'data',
    question: "Avez-vous un data warehouse / lakehouse centralisé pour vos données métier ?",
    hint: 'Snowflake, BigQuery, Databricks, Redshift, Postgres central, etc.',
    choices: [
      { label: 'Non, données éparpillées dans des silos applicatifs', score: 0 },
      { label: 'On a commencé à centraliser, peu mature', score: 1 },
      { label: 'Data warehouse en place, alimenté irrégulièrement', score: 2 },
      { label: 'Data warehouse mature, ingestion automatisée quotidienne', score: 3 },
      { label: 'Lakehouse + couche sémantique + lineage + qualité monitorée', score: 4 },
    ],
  },
  {
    dimension: 'data',
    question: "Comment qualifieriez-vous la qualité de vos données métier (complétude, fraîcheur, fiabilité) ?",
    choices: [
      { label: 'Mauvaise — beaucoup de saisie manuelle, peu fiable', score: 0 },
      { label: 'Variable selon les sources', score: 1 },
      { label: 'Correcte pour le reporting, pas auditée', score: 2 },
      { label: 'Bonne — process qualité en place sur les sources critiques', score: 3 },
      { label: 'Excellente — observabilité data, tests automatisés (Great Expectations, Soda, dbt tests)', score: 4 },
    ],
  },
  {
    dimension: 'data',
    question: "Vos données textuelles non structurées (contrats, emails, tickets) sont-elles indexées et accessibles ?",
    choices: [
      { label: 'Non, pas du tout', score: 0 },
      { label: 'Stockées mais non indexées', score: 1 },
      { label: 'Indexation basique (search texte)', score: 2 },
      { label: 'Vector store + embeddings sur certains corpus', score: 3 },
      { label: 'Pipeline RAG en production sur 1+ corpus critique', score: 4 },
    ],
  },

  // ÉQUIPE
  {
    dimension: 'team',
    question: "Avez-vous au moins une personne en interne dédiée à l'IA / data science ?",
    choices: [
      { label: 'Non, personne en interne', score: 0 },
      { label: 'Référent partiel sur un autre rôle', score: 1 },
      { label: '1 Data Analyst / Data Scientist à temps plein', score: 2 },
      { label: 'Équipe Data/IA dédiée (2-5 personnes)', score: 3 },
      { label: 'Équipe IA structurée (Data Eng + DS + ML Eng + MLOps + AI Eng)', score: 4 },
    ],
  },
  {
    dimension: 'team',
    question: "Vos équipes produit / métier ont-elles été formées à l'IA générative ?",
    choices: [
      { label: 'Non, pas du tout', score: 0 },
      { label: 'Quelques personnes auto-formées de leur côté', score: 1 },
      { label: 'Sessions de sensibilisation occasionnelles', score: 2 },
      { label: 'Plan de formation IA mis en place sur 1+ équipe', score: 3 },
      { label: 'Programme de formation IA à l\'échelle de l\'entreprise', score: 4 },
    ],
  },
  {
    dimension: 'team',
    question: "Comment recrutez-vous vos profils IA en PACA ?",
    choices: [
      { label: 'On n\'a pas encore recruté en IA', score: 0 },
      { label: 'On publie des annonces, peu de réponses qualifiées', score: 1 },
      { label: 'On chasse via LinkedIn en interne', score: 2 },
      { label: 'On travaille avec un cabinet généraliste', score: 3 },
      { label: 'On travaille avec un cabinet spécialisé IA + sourcing interne', score: 4 },
    ],
  },

  // INFRA
  {
    dimension: 'infra',
    question: "Avez-vous une plateforme cloud (AWS / GCP / Azure / OVH / Scaleway) opérationnelle ?",
    choices: [
      { label: 'Non, 100% on-premise sans cloud', score: 0 },
      { label: 'Quelques services cloud isolés', score: 1 },
      { label: 'Une plateforme cloud principale, sans IaC structurée', score: 2 },
      { label: 'Cloud + Infrastructure as Code (Terraform / CDK)', score: 3 },
      { label: 'Multi-cloud ou cloud + on-prem hybride, FinOps en place', score: 4 },
    ],
  },
  {
    dimension: 'infra',
    question: "Vos modèles / produits IA bénéficient-ils d'un pipeline CI/CD ?",
    choices: [
      { label: 'Non, déploiement manuel', score: 0 },
      { label: 'CI/CD applicatif classique mais pas pour les modèles', score: 1 },
      { label: 'CI/CD adapté pour 1 produit IA', score: 2 },
      { label: 'CI/CD ML standardisé (MLflow / W&B + GitHub Actions)', score: 3 },
      { label: 'Plateforme MLOps complète (Kubeflow / Vertex AI / SageMaker pipelines)', score: 4 },
    ],
  },
  {
    dimension: 'infra',
    question: "Disposez-vous d'accès GPU (interne ou cloud) pour entraîner ou servir des modèles IA ?",
    choices: [
      { label: 'Non, pas d\'accès GPU', score: 0 },
      { label: 'GPUs occasionnels via Colab / spot instances', score: 1 },
      { label: 'Quota GPU cloud réservé pour les besoins ponctuels', score: 2 },
      { label: 'Cluster GPU partagé, scheduler en place', score: 3 },
      { label: 'Cluster GPU + autoscaling + monitoring (DCGM), FinOps GPU', score: 4 },
    ],
  },

  // GOUVERNANCE
  {
    dimension: 'governance',
    question: "Avez-vous formalisé une politique d'usage de l'IA (charte interne) ?",
    choices: [
      { label: 'Non, rien de formalisé', score: 0 },
      { label: 'Communication informelle (canal Slack, note de service)', score: 1 },
      { label: 'Charte interne diffusée, peu suivie', score: 2 },
      { label: 'Charte interne + référents IA dans les équipes', score: 3 },
      { label: 'Charte + comité IA + audits + reporting régulier', score: 4 },
    ],
  },
  {
    dimension: 'governance',
    question: "Vos équipes connaissent-elles les obligations de l'AI Act européen ?",
    choices: [
      { label: 'Non, sujet non abordé', score: 0 },
      { label: 'Sensibilisation très partielle', score: 1 },
      { label: 'Identification des systèmes IA à risque potentiel', score: 2 },
      { label: 'Cartographie complète + plan de mise en conformité', score: 3 },
      { label: 'Conformité AI Act intégrée à la roadmap, suivie par juridique', score: 4 },
    ],
  },
  {
    dimension: 'governance',
    question: "Comment gérez-vous les risques liés aux LLM (hallucinations, données sensibles, prompt injection) ?",
    choices: [
      { label: 'Non identifiés', score: 0 },
      { label: 'Conscience floue, pas de garde-fous techniques', score: 1 },
      { label: 'Garde-fous basiques (filtres, prompts système, PII scrubbing)', score: 2 },
      { label: 'Eval framework + monitoring des sorties (Langfuse / Phoenix)', score: 3 },
      { label: 'Red teaming régulier + guardrails industriels (NeMo, Guardrails AI)', score: 4 },
    ],
  },
];

// =========================================================================
// Composant principal
// =========================================================================

export default function QuizAiReadinessPage() {
  const [phase, setPhase] = useState<'intro' | 'questions' | 'form' | 'done'>('intro');
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [currentIdx, setCurrentIdx] = useState(0);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Quiz',
    name: 'Quiz AI Readiness Score — Provence-Alpes-Côte d\'Azur',
    description:
      "Quiz interactif en 15 questions pour évaluer la maturité IA de votre entreprise sur 5 dimensions : stratégie, données, équipe, infra, gouvernance. Rapport personnalisé envoyé par email.",
    educationalLevel: 'professional',
    about: { '@type': 'Thing', name: 'AI Readiness Assessment' },
    publisher: { '@type': 'Organization', name: 'Recruteurs.io' },
    url: 'https://recruteurs.io/ia-paca/quiz-ai-readiness',
  };

  const totalQuestions = QUESTIONS.length;
  const allAnswered = Object.keys(answers).length === totalQuestions;

  return (
    <Layout
      title="Quiz AI Readiness Score — Maturité IA de votre entreprise | recruteurs.io"
      description="Évaluez la maturité IA de votre entreprise en 5 minutes. Quiz de 15 questions sur 5 dimensions : stratégie, données, équipe, infra, gouvernance. Rapport personnalisé par email."
      keywords="AI readiness score, maturité IA entreprise, audit IA gratuit, quiz IA, diagnostic IA PACA, évaluation maturité intelligence artificielle"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Quiz AI Readiness', href: '/ia-paca/quiz-ai-readiness' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/40">
        <div className="mx-auto max-w-3xl px-6 py-10 lg:py-16">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Retour au hub IA PACA
          </Link>

          {phase === 'intro' && <IntroScreen onStart={() => setPhase('questions')} />}

          {phase === 'questions' && (
            <QuestionsScreen
              currentIdx={currentIdx}
              setCurrentIdx={setCurrentIdx}
              answers={answers}
              setAnswers={setAnswers}
              onComplete={() => setPhase('form')}
              allAnswered={allAnswered}
            />
          )}

          {phase === 'form' && (
            <FormScreen
              answers={answers}
              onSubmitted={() => setPhase('done')}
              onBack={() => setPhase('questions')}
            />
          )}

          {phase === 'done' && <DoneScreen answers={answers} />}
        </div>
      </div>
    </Layout>
  );
}

// =========================================================================
// Sous-composants
// =========================================================================

function IntroScreen({ onStart }: { onStart: () => void }) {
  return (
    <div className="mt-8">
      <div className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-violet-700 backdrop-blur">
        <Sparkles className="h-3 w-3" />
        Outil gratuit · 5 minutes
      </div>

      <h1 className="mt-6 text-3xl font-bold leading-[1.15] tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
        Quel est votre{' '}
        <span className="bg-gradient-to-r from-pink-600 via-orange-500 to-violet-600 bg-clip-text text-transparent">
          AI Readiness Score
        </span>{' '}
        ?
      </h1>

      <p className="mt-5 text-lg leading-relaxed text-slate-600">
        15 questions calibrées sur ce qu'on observe vraiment en PACA. Vous
        obtenez votre score sur 100, votre profil parmi 5 niveaux de
        maturité, et un rapport personnalisé envoyé par email avec les 3
        actions prioritaires à lancer dans les 90 jours.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-5">
        {DIMENSIONS.map((d) => (
          <div
            key={d.key}
            className="rounded-2xl border border-slate-200 bg-white p-4 text-center"
          >
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
              <d.Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-3 text-sm font-bold text-slate-900">{d.label}</h3>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-bold text-slate-900">
          Ce que vous allez obtenir
        </h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>Un score global sur 100 + score par dimension (5 axes)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>
              Votre profil de maturité (Explorateur, Expérimentateur,
              Industrialisateur, Optimisateur, Pionnier)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>
              Un rapport PDF personnalisé envoyé par email avec 3 actions
              prioritaires sur 90 jours
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>
              Recommandations de profils à recruter selon votre niveau de
              maturité actuel
            </span>
          </li>
        </ul>
      </div>

      <button
        onClick={onStart}
        className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-pink-200/50 transition hover:shadow-xl"
      >
        Démarrer le quiz
        <ArrowRight className="h-4 w-4" />
      </button>
      <p className="mt-3 text-xs text-slate-500">
        Aucune création de compte. Aucune carte bancaire. ~5 minutes.
      </p>
    </div>
  );
}

function QuestionsScreen({
  currentIdx,
  setCurrentIdx,
  answers,
  setAnswers,
  onComplete,
  allAnswered,
}: {
  currentIdx: number;
  setCurrentIdx: (i: number) => void;
  answers: Record<number, number>;
  setAnswers: (a: Record<number, number>) => void;
  onComplete: () => void;
  allAnswered: boolean;
}) {
  const q = QUESTIONS[currentIdx];
  const dim = DIMENSIONS.find((d) => d.key === q.dimension)!;
  const totalQuestions = QUESTIONS.length;
  const isLast = currentIdx === totalQuestions - 1;

  function handleSelect(score: number) {
    const updated = { ...answers, [currentIdx]: score };
    setAnswers(updated);
    if (!isLast) {
      // Avance automatiquement après une sélection
      setTimeout(() => setCurrentIdx(currentIdx + 1), 250);
    }
  }

  return (
    <div className="mt-8">
      {/* Barre de progression */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          Question <strong className="text-slate-900">{currentIdx + 1}</strong> /{' '}
          {totalQuestions}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <dim.Icon className="h-3.5 w-3.5 text-violet-600" />
          {dim.label}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-pink-600 to-violet-600 transition-all duration-300"
          style={{ width: `${((currentIdx + 1) / totalQuestions) * 100}%` }}
        />
      </div>

      {/* Question */}
      <h2 className="mt-8 text-2xl font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl">
        {q.question}
      </h2>
      {q.hint && (
        <p className="mt-2 text-sm italic text-slate-500">{q.hint}</p>
      )}

      {/* Choix */}
      <div className="mt-6 space-y-2">
        {q.choices.map((c, i) => {
          const isSelected = answers[currentIdx] === c.score;
          return (
            <button
              key={c.label}
              onClick={() => handleSelect(c.score)}
              className={`group flex w-full items-start gap-3 rounded-2xl border-2 p-4 text-left text-sm transition ${
                isSelected
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/30'
              }`}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                  isSelected
                    ? 'border-violet-500 bg-violet-500 text-white'
                    : 'border-slate-300 bg-white text-slate-400'
                }`}
              >
                {String.fromCharCode(65 + i)}
              </span>
              <span
                className={
                  isSelected ? 'font-semibold text-violet-900' : 'text-slate-700'
                }
              >
                {c.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="mt-8 flex items-center justify-between">
        <button
          disabled={currentIdx === 0}
          onClick={() => setCurrentIdx(currentIdx - 1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 disabled:opacity-40"
        >
          <ArrowLeftCircle className="h-4 w-4" />
          Précédente
        </button>

        {isLast ? (
          <button
            disabled={!allAnswered}
            onClick={onComplete}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-pink-200/50 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            Voir mon score
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            disabled={answers[currentIdx] === undefined}
            onClick={() => setCurrentIdx(currentIdx + 1)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-40"
          >
            Suivante
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function FormScreen({
  answers,
  onSubmitted,
  onBack,
}: {
  answers: Record<number, number>;
  onSubmitted: () => void;
  onBack: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scoreData = useMemo(() => computeScore(answers), [answers]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const cleanFirst = firstName.trim();
    const cleanLast = lastName.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanCompany = company.trim();

    if (!cleanFirst || !cleanLast || !cleanEmail) {
      setError('Prénom, nom et email sont requis.');
      setSubmitting(false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('Adresse email invalide.');
      setSubmitting(false);
      return;
    }

    try {
      const { error: insertError } = await supabase.from('leads').insert([
        {
          first_name: cleanFirst,
          last_name: cleanLast,
          email: cleanEmail,
          company: cleanCompany,
          source: `quiz_ai_readiness_score_${scoreData.profile.slug}`,
        },
      ]);

      if (insertError) {
        console.error('[quiz] insert lead error:', insertError.message);
        setError("Impossible d'enregistrer votre demande. Réessayez.");
        setSubmitting(false);
        return;
      }

      // Notification email best-effort, on lui passe le score
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

      if (supabaseUrl && supabaseAnonKey) {
        fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/whitepaper-notify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseAnonKey}`,
            apikey: supabaseAnonKey,
          },
          body: JSON.stringify({
            paper: {
              slug: 'quiz-ai-readiness-score',
              title: `Quiz AI Readiness — Score ${scoreData.globalScore}/100 (${scoreData.profile.label})`,
            },
            lead: {
              firstName: cleanFirst,
              lastName: cleanLast,
              email: cleanEmail,
              company: cleanCompany,
            },
            metadata: {
              submittedAt: new Date().toISOString(),
              userAgent:
                typeof navigator !== 'undefined' ? navigator.userAgent : '',
              referrer:
                typeof window !== 'undefined' ? window.location.pathname : '',
              globalScore: scoreData.globalScore,
              profile: scoreData.profile.label,
              dimensions: scoreData.dimensionScores,
            },
          }),
          keepalive: true,
        }).catch((err) => {
          console.warn('[quiz] notify email failed:', err);
        });
      }

      onSubmitted();
    } catch (err) {
      console.error('[quiz] submit error:', err);
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-8">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900"
      >
        <ArrowLeftCircle className="h-3.5 w-3.5" />
        Modifier mes réponses
      </button>

      <h2 className="mt-6 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        Votre score est prêt.
      </h2>
      <p className="mt-3 text-base text-slate-600">
        Indiquez votre email pour recevoir le rapport personnalisé avec les
        3 actions prioritaires à mettre en place dans les 90 jours.
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-8 rounded-2xl border-2 border-violet-200 bg-white p-6 shadow-lg"
      >
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-violet-600" />
          <h3 className="text-base font-bold text-slate-900">
            Recevoir mon rapport personnalisé
          </h3>
        </div>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Prénom"
              required
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Nom"
              required
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email professionnel"
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Entreprise"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
        </div>

        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-4 py-3 text-sm font-semibold text-white shadow-md shadow-pink-200/50 transition hover:shadow-lg disabled:opacity-60"
        >
          {submitting ? (
            'Envoi en cours...'
          ) : (
            <>
              <Download className="h-4 w-4" />
              Voir mon score & recevoir le rapport
            </>
          )}
        </button>

        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
          Aucun spam, aucun partage avec un tiers, conformité RGPD.
          Désinscription en 1 clic à tout moment.
        </p>
      </form>
    </div>
  );
}

function DoneScreen({ answers }: { answers: Record<number, number> }) {
  const scoreData = useMemo(() => computeScore(answers), [answers]);

  return (
    <div className="mt-8">
      <div className="rounded-3xl border-2 border-violet-200 bg-gradient-to-br from-white via-violet-50/30 to-pink-50/20 p-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />
            Rapport envoyé par email
          </div>

          <h2 className="mt-5 text-base font-bold uppercase tracking-wider text-slate-500">
            Votre AI Readiness Score
          </h2>
          <div className="mt-2 text-6xl font-bold leading-none tracking-tight text-slate-900 sm:text-7xl">
            {scoreData.globalScore}
            <span className="text-3xl text-slate-400">/100</span>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-violet-600 px-4 py-1.5 text-sm font-bold text-white">
            <Sparkles className="h-4 w-4" />
            Profil : {scoreData.profile.label}
          </div>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-600">
            {scoreData.profile.description}
          </p>
        </div>

        {/* Scores par dimension */}
        <div className="mt-10 space-y-3">
          {DIMENSIONS.map((d) => {
            const score = scoreData.dimensionScores[d.key];
            const pct = Math.round((score / 12) * 100);
            return (
              <div key={d.key} className="rounded-xl bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <d.Icon className="h-4 w-4 text-violet-600" />
                    <span className="text-sm font-semibold text-slate-900">
                      {d.label}
                    </span>
                  </div>
                  <span className="text-sm tabular-nums font-bold text-slate-700">
                    {score}/12 ({pct}%)
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full bg-gradient-to-r from-pink-500 to-violet-600 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommandations */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-violet-600" />
          <h3 className="text-base font-bold text-slate-900">
            Vos 3 actions prioritaires
          </h3>
        </div>
        <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700">
          {scoreData.profile.actions.map((a, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                {i + 1}
              </span>
              <span>{a}</span>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-xs italic text-slate-500">
          Le rapport PDF complet (envoyé par email) détaille chacune de ces
          actions, les profils à recruter en priorité selon votre niveau, et
          un plan 90 jours.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-8 rounded-2xl border-2 border-dashed border-pink-200 bg-pink-50/50 p-6 text-center">
        <h3 className="text-base font-bold text-slate-900">
          Envie d'en parler avec un humain ?
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          Brief 30 min gratuit pour discuter de votre stratégie de
          recrutement IA en fonction de votre profil ({scoreData.profile.label}).
        </p>
        <Link
          to="/reservation"
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-pink-200/50 transition hover:shadow-lg"
        >
          Réserver mes 30 minutes
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

// =========================================================================
// Logique scoring
// =========================================================================

interface Profile {
  slug: string;
  label: string;
  description: string;
  actions: string[];
  minScore: number;
}

const PROFILES: Profile[] = [
  {
    slug: 'explorateur',
    label: 'Explorateur',
    minScore: 0,
    description:
      "Vous découvrez l'IA. C'est normal — la majorité des entreprises PACA sont à ce stade en 2026. L'enjeu : passer d'une intuition à un POC concret en 90 jours.",
    actions: [
      "Cadrer 1 cas d'usage IA avec un sponsor exécutif et un budget POC (10-30 k€).",
      "Identifier vos données critiques : où sont-elles, qui les possède, quelle qualité.",
      "Former 3-5 personnes clés à l'IA générative (1 journée + suivi 30 jours).",
    ],
  },
  {
    slug: 'experimentateur',
    label: 'Expérimentateur',
    minScore: 25,
    description:
      "Vous avez lancé vos premiers POC. Bravo. Le défi maintenant : éviter le cimetière des POC. Industrialiser ce qui marche, abandonner ce qui ne marche pas.",
    actions: [
      "Faire un POC review : quels POC valent d'être industrialisés ? Critères : ROI clair, données dispo, sponsor engagé.",
      "Recruter un premier profil tech IA dédié (AI Engineer ou Data Scientist senior) capable de passer le POC en prod.",
      "Mettre en place un tracking minimal des coûts IA et de la performance des modèles déployés.",
    ],
  },
  {
    slug: 'industrialisateur',
    label: 'Industrialisateur',
    minScore: 45,
    description:
      "Vous avez 1+ produit IA en prod. Vous entrez maintenant dans la phase la plus structurante : scaler la plateforme, structurer l'équipe, garantir la fiabilité.",
    actions: [
      "Structurer une équipe IA dédiée (ML Eng + AI Eng + MLOps minimum) avec un Lead IA reporting au CTO ou COO.",
      "Bâtir une plateforme MLOps minimale (CI/CD, tracking, monitoring, alerting) pour soutenir 5+ modèles en prod.",
      "Formaliser une charte IA interne + identifier les systèmes potentiellement à risque AI Act.",
    ],
  },
  {
    slug: 'optimisateur',
    label: 'Optimisateur',
    minScore: 60,
    description:
      "Votre maturité IA est solide. Vous êtes parmi les ~10% d'entreprises PACA les plus avancées. L'enjeu : optimiser, sécuriser, ne pas se faire dépasser.",
    actions: [
      "Lancer un programme FinOps IA (coûts cloud + inférence + GPU) — peut diviser la facture par 2 sur 6 mois.",
      "Mettre en place un eval framework robuste + red teaming régulier sur vos systèmes LLM critiques.",
      "Recruter sur les profils rares : MLOps Senior, Architecte IA, Agentic AI Engineer — l'avantage compétitif sur 24 mois.",
    ],
  },
  {
    slug: 'pionnier',
    label: 'Pionnier',
    minScore: 80,
    description:
      "Vous êtes à l'état de l'art. Très peu d'entreprises PACA atteignent ce niveau. L'enjeu : maintenir cette avance et capter la prochaine vague (agents autonomes, multimodalité).",
    actions: [
      "Investir dans la R&D : fine-tuning, agents, multimodal — c'est là que se joue la différenciation 2027-2028.",
      "Devenir un employeur de référence IA en PACA : marque employeur tech, contributions open-source, talks, papers.",
      "Structurer votre conformité AI Act dès maintenant : c'est un avantage compétitif quand vos concurrents seront en retard.",
    ],
  },
];

interface ScoreData {
  globalScore: number; // /100
  dimensionScores: Record<Dimension, number>; // /12 chacun (3 questions × 4)
  profile: Profile;
}

function computeScore(answers: Record<number, number>): ScoreData {
  const dimensionScores: Record<Dimension, number> = {
    strategy: 0,
    data: 0,
    team: 0,
    infra: 0,
    governance: 0,
  };

  QUESTIONS.forEach((q, idx) => {
    dimensionScores[q.dimension] += answers[idx] ?? 0;
  });

  // Score global : moyenne des 5 dimensions sur 12 → sur 100
  const totalRaw = Object.values(dimensionScores).reduce((s, v) => s + v, 0);
  const maxRaw = 5 * 12; // 5 dimensions × 12 points max chacune
  const globalScore = Math.round((totalRaw / maxRaw) * 100);

  // Sélectionne le profil
  const profile = [...PROFILES]
    .reverse()
    .find((p) => globalScore >= p.minScore)!;

  return { globalScore, dimensionScores, profile };
}
